import { FC, useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useNavigate, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import { RENTER_PATHS, buildBookingPath } from 'src/routes/renterPaths';
import { zoneAbbreviation } from 'src/components/Compliance/Appointments/CalendarView';
import { RootState } from 'src/redux/rootReducerType';
import { Certification } from 'src/types/Certification';
import {
  RenterAppointment,
  cancelRenterAppointment,
  summarizeRenterUnits,
  toRenterApiError,
  useRenterAppointments,
} from './useRenterAppointments';

dayjs.extend(utc);
dayjs.extend(timezone);

const STATUS_LABEL: Record<RenterAppointment['status'], string> = {
  scheduled: 'Scheduled',
  rescheduled: 'Moved',
  completed: 'Done',
  no_show: 'Missed',
  cancelled: 'Cancelled',
};
const STATUS_COLOR: Record<
  RenterAppointment['status'],
  'info' | 'warning' | 'success' | 'error' | 'default'
> = {
  scheduled: 'info',
  rescheduled: 'warning',
  completed: 'success',
  no_show: 'error',
  cancelled: 'default',
};

export const formatWhen = (a: RenterAppointment): string => {
  const start = dayjs(a.start_at).tz(a.timezone);
  return `${start.format('dddd, MM/DD/YYYY')} at ${start.format(
    'h:mm A'
  )} ${zoneAbbreviation(a.timezone)}`;
};

const AppointmentCard: FC<{
  appointment: RenterAppointment;
  onCancel?: (a: RenterAppointment) => void;
  onReschedule?: (a: RenterAppointment) => void;
}> = ({ appointment: a, onCancel, onReschedule }) => {
  const cancelBy = dayjs(a.cancel_by).tz(a.timezone);
  const units = summarizeRenterUnits(a.units);
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Typography variant="subtitle1">{a.category_name}</Typography>
            <Chip
              size="small"
              color={STATUS_COLOR[a.status]}
              label={STATUS_LABEL[a.status]}
            />
          </Stack>
          <Typography variant="body1" fontWeight={600}>
            {formatWhen(a)}
          </Typography>
          {units.labels && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Units: {units.labels}
              </Typography>
              {units.extra > 0 && (
                <Chip size="small" label={`+${units.extra}`} />
              )}
            </Stack>
          )}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PlaceOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {a.location || a.property_name}
              </Typography>
            </Stack>
            {a.host && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PersonOutlineIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {a.host.name}
                </Typography>
              </Stack>
            )}
          </Stack>
          {a.booking_instructions && a.status === 'scheduled' && (
            <Typography variant="body2">{a.booking_instructions}</Typography>
          )}
          {a.status === 'scheduled' && (onCancel || onReschedule) && (
            <Stack spacing={1} pt={0.5}>
              <Stack direction="row" spacing={1}>
                {onReschedule && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!a.can_reschedule}
                    onClick={() => onReschedule(a)}
                  >
                    Move
                  </Button>
                )}
                {onCancel && (
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    disabled={!a.can_cancel}
                    onClick={() => onCancel(a)}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {a.can_cancel
                  ? `You can cancel or move this until ${cancelBy.format(
                      'h:mm A'
                    )} on ${cancelBy.format('MM/DD/YYYY')}.`
                  : 'It is too close to change this online. Please call the office.'}
              </Typography>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

/** /renter/appointments — the renter's upcoming and past visits. */
const RenterAppointmentsPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const justBooked = searchParams.get('booked') === '1';
  const { upcoming, past, loading, error, unavailable, refresh } =
    useRenterAppointments();
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[] | undefined;
  const [cancelTarget, setCancelTarget] = useState<RenterAppointment | null>(
    null
  );
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const activeCertIds = useMemo(
    () =>
      (certifications ?? [])
        .filter((c) => c.status?.renter_active !== false)
        .map((c) => c.id),
    [certifications]
  );

  const handleReschedule = useCallback(
    (a: RenterAppointment) => {
      navigate(buildBookingPath(a.certification, a.id, a.category));
    },
    [navigate]
  );

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelTarget) {
      return;
    }
    setCancelling(true);
    setActionError(null);
    try {
      await cancelRenterAppointment(cancelTarget.id);
      setCancelTarget(null);
      refresh();
    } catch (err: unknown) {
      const apiError = toRenterApiError(err);
      setActionError(
        apiError.code === 'cutoff_passed'
          ? 'It is too close to your appointment to cancel online. Please call the office.'
          : apiError.detail
      );
      setCancelTarget(null);
    } finally {
      setCancelling(false);
    }
  }, [cancelTarget, refresh]);

  const dismissBooked = useCallback(() => {
    searchParams.delete('booked');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <Box>
      <PageHeader
        title="Appointments"
        icon={<EventAvailableIcon />}
        backRoute={`/${RENTER_PATHS.HOME}`}
        subtitle="Your visits, tours, and meetings"
        hideBackOnDesktop
      />
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Stack spacing={2.5}>
          {justBooked && (
            <Alert severity="success" onClose={dismissBooked}>
              You&apos;re booked. We&apos;ll text and email you a reminder.
            </Alert>
          )}
          {error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={refresh}>
                  Try again
                </Button>
              }
            >
              {error}
            </Alert>
          )}
          {actionError && <Alert severity="warning">{actionError}</Alert>}

          {loading && upcoming.length === 0 && past.length === 0 ? (
            <Box py={6} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : unavailable ? (
            <Alert severity="info">
              Online appointments are not open for your home yet. Call the
              office to set up a time.
            </Alert>
          ) : (
            <>
              <Stack spacing={1.5}>
                <Typography variant="h6">Coming up</Typography>
                {upcoming.length === 0 ? (
                  <Alert severity="info" icon={false}>
                    You have no upcoming appointments.
                  </Alert>
                ) : (
                  upcoming.map((a) => (
                    <AppointmentCard
                      key={a.id}
                      appointment={a}
                      onCancel={setCancelTarget}
                      onReschedule={handleReschedule}
                    />
                  ))
                )}
                {activeCertIds.length > 0 && (
                  <Button
                    variant="contained"
                    onClick={() => navigate(buildBookingPath(activeCertIds[0]))}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Book a Visit
                  </Button>
                )}
              </Stack>

              {past.length > 0 && (
                <Stack spacing={1.5}>
                  <Typography variant="h6">Past</Typography>
                  {past.map((a) => (
                    <AppointmentCard key={a.id} appointment={a} />
                  ))}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Container>

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => setCancelTarget(null)}
      >
        <DialogTitle>Cancel this appointment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelTarget
              ? `Your ${cancelTarget.category_name} on ${formatWhen(
                  cancelTarget
                )} will be cancelled. You can book a new time any time.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setCancelTarget(null)}
            disabled={cancelling}
          >
            Keep It
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmCancel}
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={20} /> : 'Cancel Appointment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RenterAppointmentsPage;
